import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

const BASE =
  'inline-flex items-center justify-center gap-2 font-semibold rounded-ops-md select-none ' +
  'transition duration-ops ease-ops active:translate-y-px ' +
  'disabled:opacity-50 disabled:pointer-events-none';

const VARIANT: Record<Variant, string> = {
  primary: 'bg-ops-primary text-ops-on-primary hover:bg-ops-primary-600 shadow-ops-card',
  secondary: 'bg-ops-surface text-ops-text border border-ops-border-strong hover:bg-ops-surface-2',
  ghost: 'bg-transparent text-ops-text-secondary hover:bg-ops-surface-2 hover:text-ops-text',
};

const SIZE: Record<Size, string> = {
  sm: 'h-9 px-3 text-ops-label',
  md: 'h-11 px-4 text-ops-body',
  lg: 'h-12 px-5 text-ops-body',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button className={cn(BASE, VARIANT[variant], SIZE[size], className)} {...rest}>
      {leftIcon}
      {children}
      {rightIcon}
    </button>
  );
}

const ICON_SIZE: Record<Size, string> = {
  sm: 'h-9 w-9',
  md: 'h-11 w-11',
  lg: 'h-12 w-12',
};

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** обязателен — иконочная кнопка без текста */
  'aria-label': string;
  children: ReactNode;
}

export function IconButton({ variant = 'ghost', size = 'md', className, children, ...rest }: IconButtonProps) {
  return (
    <button className={cn(BASE, VARIANT[variant], ICON_SIZE[size], 'px-0', className)} {...rest}>
      {children}
    </button>
  );
}
